import React from 'react';
import type { MediaFormat } from '@/hooks/useMediaFormats';

/**
 * Filter media formats by case-insensitive partial matching against name.
 *
 * Returns all formats when query is empty or whitespace-only.
 * Results are scored: exact prefix match = 1 (highest), contains match = 2.
 * Sorted by score then alphabetically by name.
 */
export function filterMediaFormats(formats: MediaFormat[], query: string): MediaFormat[] {
  const q = query.trim().toLowerCase();
  if (!q) return formats;

  const scored: { format: MediaFormat; score: number }[] = [];

  for (const f of formats) {
    const nameLower = f.name.toLowerCase();

    if (nameLower.startsWith(q)) {
      scored.push({ format: f, score: 1 }); // Exact prefix match — top priority
    } else if (nameLower.includes(q)) {
      scored.push({ format: f, score: 2 }); // Contains match
    }
  }

  // Sort by score (lower = better), then alphabetically by name
  scored.sort((a, b) => {
    if (a.score !== b.score) return a.score - b.score;
    return a.format.name.localeCompare(b.format.name);
  });

  return scored.map((s) => s.format);
}

/**
 * Resolve a media format text value (from spreadsheet paste/upload) to a media_format_id.
 * Exact match (case-insensitive, trimmed) against name.
 * Returns the id or null if no match.
 */
export function resolveMediaFormatId(formats: MediaFormat[], text: string): number | null {
  const q = text.trim().toLowerCase();
  if (!q) return null;

  for (const f of formats) {
    if (f.name.toLowerCase() === q) return f.id;
  }

  return null;
}

/**
 * Highlight the first occurrence of `query` within `text` by wrapping
 * the matched portion in a <strong> tag. Returns the original text
 * as a plain string when there is no match or query is empty.
 */
export function highlightMediaFormatMatch(text: string, query: string): React.ReactNode {
  const q = query.trim();
  if (!q) return text;

  const lowerText = text.toLowerCase();
  const lowerQuery = q.toLowerCase();
  const idx = lowerText.indexOf(lowerQuery);

  if (idx === -1) return text;

  const before = text.slice(0, idx);
  const match = text.slice(idx, idx + q.length);
  const after = text.slice(idx + q.length);

  return React.createElement(React.Fragment, null,
    before,
    React.createElement('strong', null, match),
    after,
  );
}
